'use client';

import React, { useEffect } from 'react';
import './Modal.css';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface ModalProps {
  children: React.ReactNode;
  closeOnBackdropClick?: boolean;
  isOpen: boolean;
  onClose: () => void;
  header?: React.ReactNode;
  variant?: 'light' | 'dark';
  className?: string;
}

export function Modal({
  children,
  closeOnBackdropClick,
  isOpen,
  onClose,
  header,
  variant = 'light',
  className,
}: ModalProps) {
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  const handleEscapeKey = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  const memoizedHandleOpen = React.useCallback(handleOpen, [handleEscapeKey]);

  useEffect(() => {
    if (isOpen) {
      memoizedHandleOpen();
    }
  }, [isOpen, memoizedHandleOpen]);

  function handleOpen() {
    dialogRef.current?.showModal();
    document.addEventListener('keydown', handleEscapeKey);

    return () => document.removeEventListener('keydown', handleEscapeKey);
  }

  return (
    <>
      {isOpen && (
        <dialog ref={dialogRef} className="modal">
          <div
            className={clsx(
              `modal-box overflow-y-auto border border-dark-background ${variant === 'dark' ? 'bg-[#141415]' : 'bg-white'} p-6 md:p-10`,
              className,
            )}
          >
            <form method="dialog">{header ? header : <ModalHeader onClose={onClose} />}</form>
            {children}
          </div>
          {closeOnBackdropClick && (
            <form method="dialog" className="modal-backdrop">
              <button onClick={onClose}>close</button>
            </form>
          )}
        </dialog>
      )}
    </>
  );
}

export function ModalHeader({ onClose }: { onClose: () => void }) {
  return (
    <button
      className="absolute top-2 right-2 z-10 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-gray-4 hover:bg-gray-5/50"
      onClick={onClose}
    >
      <XMarkIcon className="h-6 w-6" />
    </button>
  );
}
